/**
 * RunningProgressPanelView — pure presentation of the live panel shown while
 * a research run is in progress. Deliberately minimal (the run detail shows
 * everything else):
 *  • header: status + elapsed time
 *  • progress bar driven by plan completion (indeterminate before the plan)
 *  • the live plan (write_todos) with per-item status
 *  • one compact stats line: researcher tool calls + findings so far
 *
 * The connected `RunningProgressPanel` in `features/hypotheses` owns the
 * EventSource subscription and derives these props from the raw events.
 */

import { Loader2, Sparkles, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { useElapsedLabel } from '@/hooks/use-elapsed-label'
import type { HypothesisRunTodo } from '@/types/hypothesis-finding'
import { RunPlanList } from './RunPlanList'

export interface RunningProgressPanelViewProps {
  /** True while the SSE stream is connected; false while reconnecting. */
  isConnected: boolean
  /** Run start time (ISO) for the live elapsed timer; null hides it. */
  startedAt: string | null
  /** Deep agent's live plan (latest write_todos payload). */
  todos: HypothesisRunTodo[]
  /** Total researcher subagent tool calls observed so far. */
  researcherCallCount: number
  /** Findings surfaced so far (count only — cards live in the detail). */
  findingCount: number
}

export function RunningProgressPanelView({
  isConnected,
  startedAt,
  todos,
  researcherCallCount,
  findingCount,
}: RunningProgressPanelViewProps) {
  const { t } = useTranslation()
  const elapsed = useElapsedLabel(startedAt)

  return (
    <Card tone="primary" className="flex flex-col gap-4 p-4 md:p-5">
      {/* Header — status + elapsed. No progress bar: a research run has no
          honest percentage; the spinner, timer, and plan checkmarks carry
          the progress story. */}
      <div className="flex items-center gap-3">
        <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
        <div className="flex min-w-0 flex-1 items-center justify-between gap-3 text-xs">
          <span className="min-w-0 font-medium text-foreground">
            {isConnected ? t('hypothesisRun.traversing') : t('hypothesisRun.reconnecting')}
          </span>
          {elapsed && (
            <span className="shrink-0 font-mono tabular-nums text-muted-foreground">{elapsed}</span>
          )}
        </div>
      </div>

      {/* Live plan (write_todos) */}
      <RunPlanList todos={todos} />

      {/* Compact stats line — research activity without the noise. */}
      {(researcherCallCount > 0 || findingCount > 0) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {researcherCallCount > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Users className="size-3" />
              {t('hypothesisRun.researcherCalls', { count: researcherCallCount })}
            </span>
          )}
          {findingCount > 0 && (
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
              <Sparkles className="size-3 text-primary" />
              {t('hypothesisRun.findingsCount', { count: findingCount })}
            </span>
          )}
        </div>
      )}
    </Card>
  )
}
